import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styles from './login.module.css';
import { API_URL } from '../Utils/api';
import { GoogleLogin } from '@react-oauth/google';
import logo from '../images/logo.jpeg'
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const Login = () => {
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [rememberMe, setRememberMe] = useState(false);

    // Charger l'email mémorisé au montage
    useEffect(() => {
        const rememberedEmail = localStorage.getItem('rememberedEmail');
        if (rememberedEmail) {
            setEmail(rememberedEmail);
            setRememberMe(true);
        }
    }, []);

    const validateEmail = useCallback((email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setErrors({});

        // Validation
        const newErrors = {};
        
        if (!email.trim()) {
            newErrors.email = 'L\'adresse e-mail est requise';
        } else if (!validateEmail(email.trim())) {
            newErrors.email = 'Adresse e-mail invalide';
        }
        
        if (!password.trim()) {
            newErrors.password = 'Le mot de passe est requis';
        } else if (password.trim().length < 6) {
            newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            setIsLoading(true);

            const response = await axios.post(`${API_URL}/login`, {
                userEmail: email.trim(),
                userPassword: password.trim(),
            });

            const data = response?.data;
            
            if (data && data.token) {
                localStorage.setItem('token', data.token);
                
                if (rememberMe) {
                    localStorage.setItem('rememberedEmail', email.trim());
                } else {
                    localStorage.removeItem('rememberedEmail');
                }
                
                navigate('/home', { replace: true });
            } else {
                setErrors({ submit: data?.message || 'Erreur lors de la connexion' });
            }
        } catch (error) {
            console.error('Erreur connexion:', error.response?.data || error.message);
            
            const errorMessage = error.response?.data?.message 
                || error.response?.status === 401 
                    ? 'Email ou mot de passe incorrect' 
                    : 'Une erreur est survenue. Veuillez réessayer.';
            
            setErrors({ submit: errorMessage });
        } finally {
            setIsLoading(false);
        }
    }, [email, password, rememberMe, validateEmail, navigate]);

    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter') {
            handleSubmit(e);
        }
    }, [handleSubmit]);

    const handleGoogleLogin = async (credentialResponse) => {
        try {
            setIsLoading(true);
            const response = await axios.post(`${API_URL}/auth/google`, {
                token: credentialResponse.credential
            });
            const data = response?.data;
            if (data && data.token) {
                localStorage.setItem('token', data.token);
                navigate('/home', { replace: true });
            }
        } catch (error) {
            console.error('Erreur Google Auth:', error);
            setErrors({ submit: 'Échec de l\'authentification avec Google' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                {/* Brand - Left Side on Desktop */}
                <div className={styles.brand}>
                    <div className={styles.logo}>
                        <img src={logo} alt="Agora Logo" />
                    </div>
                    <h1 className={styles.brandName}>agora</h1>
                    <p className={styles.brandTagline}>
                        Agora vous aide à rester en contact avec votre communauté universitaire.
                    </p>
                </div>

                {/* Login Card - Right Side on Desktop */}
                <div className={styles.formWrapper}>
                    <form className={styles.form} onSubmit={handleSubmit}>
                        {/* Error Banner */}
                        {errors.submit && (
                            <div className={styles.errorBanner}>
                                <p>{errors.submit}</p>
                            </div>
                        )}

                        {/* Email Input */}
                        <div className={styles.inputGroup}>
                            <div className={`${styles.inputContainer} ${errors.email ? styles.inputError : ''}`}>
                                <input
                                    id="email"
                                    type="email"
                                    placeholder="Adresse e-mail"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
                                    }}
                                    onKeyPress={handleKeyPress}
                                    autoFocus
                                    autoComplete="email"
                                    disabled={isLoading}
                                />
                            </div>
                            {errors.email && <p className={styles.errorText}>{errors.email}</p>}
                        </div>

                        {/* Password Input */}
                        <div className={styles.inputGroup}>
                            <div className={`${styles.inputContainer} ${errors.password ? styles.inputError : ''}`}>
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Mot de passe"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        if (errors.password) setErrors(prev => ({ ...prev, password: '' }));
                                    }}
                                    onKeyPress={handleKeyPress}
                                    autoComplete="current-password"
                                    disabled={isLoading}
                                />
                                <button
                                    type="button"
                                    className={styles.eyeIcon}
                                    onClick={() => setShowPassword(!showPassword)}
                                    tabIndex={-1}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                            {errors.password && <p className={styles.errorText}>{errors.password}</p>}
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={isLoading}
                        >
                            {isLoading ? <span className={styles.spinner}></span> : "Se connecter"}
                        </button>

                        <div className={styles.divider}>
                            <span>Ou</span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                            <GoogleLogin
                                onSuccess={handleGoogleLogin}
                                onError={() => setErrors({ submit: 'Échec de connexion avec Google' })}
                                useOneTap
                            />
                        </div>

                        {/* Options */}
                        <div className={styles.options}>
                            <button
                                type="button"
                                className={styles.forgotPassword}
                                onClick={() => navigate('/forgot-password')}
                                disabled={isLoading}
                            >
                                Informations de compte oubliées ?
                            </button>
                            
                            <label className={styles.checkbox}>
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    disabled={isLoading}
                                />
                                <span>Rester connecté</span>
                            </label>
                        </div>

                        {/* Footer - Create Account Button */}
                        <div className={styles.footer}>
                            <button
                                type="button"
                                onClick={() => navigate('/signup')}
                                className={styles.link}
                                disabled={isLoading}
                            >
                                Créer nouveau compte
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Login;

